// App data: the catch-all server-to-client JSON store.
//
// Two scopes inside a client — global (`user_id IS NULL`) and per-user. Client admins are scoped
// by the session; platform admins pass `client_id`. Rows are addressed by `name` + `key`.
import { adminRequest } from "./adminRequest";
import { normalizePagination } from "../utils/appData";

const BASE = "/admin/appdata";

const encode = (value) => encodeURIComponent(value ?? "");

/**
 * The sort parameters. The API accepts `sort` / `direction` plus the aliases `sort_by`,
 * `order_by`, `sort_dir` and `dir`; send them all so whichever name the server reads, the order
 * matches the column the user clicked.
 */
const sortQuery = (sort = null, direction = null) => ({
  sort: sort || null,
  sort_by: sort || null,
  order_by: sort || null,
  direction: direction || null,
  sort_dir: direction || null,
  dir: direction || null,
});

const query = (params = {}) => {
  const search = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.set(key, String(value));
  });

  const text = search.toString();
  return text ? `?${text}` : "";
};

/* ------------------------------------------------------------------ global */

export const getGlobalGroups = async ({
  clientId = null,
  page = 1,
  pageSize = 100,
  sort = null,
  direction = null,
} = {}) => {
  const result = await adminRequest(
    "GET",
    `${BASE}/global/groups${query({
      client_id: clientId,
      page,
      page_size: pageSize,
      ...sortQuery(sort, direction),
    })}`
  );

  return {
    ...result,
    groups: Array.isArray(result?.groups) ? result.groups : [],
    pagination: normalizePagination(result, { page, pageSize }),
  };
};

export const listGlobalRows = async ({
  clientId = null,
  name = null,
  q = null,
  page = 1,
  pageSize = 50,
  sort = null,
  direction = null,
} = {}) => {
  const result = await adminRequest(
    "GET",
    `${BASE}/global${query({
      client_id: clientId,
      name,
      q,
      page,
      page_size: pageSize,
      ...sortQuery(sort, direction),
    })}`
  );

  return {
    ...result,
    records: Array.isArray(result?.records) ? result.records : [],
    total: Number(result?.total ?? 0),
    count: Number(result?.count ?? 0),
    pagination: normalizePagination(result, { page, pageSize }),
    sort: result?.sort ?? sort,
    direction: result?.direction ?? direction,
  };
};

export const getGlobalRow = async (name, key, { clientId = null } = {}) =>
  adminRequest("GET", `${BASE}/global/${encode(name)}/${encode(key)}${query({ client_id: clientId })}`);

/** `mode` is `upsert` (default) or `create` (409 when the row already exists). */
export const saveGlobalRow = async (payload, { clientId = null } = {}) =>
  adminRequest("POST", `${BASE}/global`, {
    mode: "upsert",
    ...payload,
    ...(clientId ? { client_id: clientId } : {}),
  });

export const updateGlobalRow = async (name, key, payload, { clientId = null } = {}) =>
  adminRequest("PUT", `${BASE}/global/${encode(name)}/${encode(key)}${query({ client_id: clientId })}`, payload);

export const deleteGlobalRow = async (name, key, { clientId = null, force = false } = {}) =>
  adminRequest(
    "DELETE",
    `${BASE}/global/${encode(name)}/${encode(key)}${query({ client_id: clientId, force: force ? "true" : null })}`
  );

/* -------------------------------------------------------------------- user */

/** A user's rows, optionally mixed with the client's global rows. */
export const getUserAppData = async (
  userPublicId,
  {
    clientId = null,
    includeGlobal = false,
    name = null,
    key = null,
    page = 1,
    pageSize = 50,
    sort = null,
    direction = null,
  } = {}
) => {
  const result = await adminRequest(
    "GET",
    `${BASE}/users/${encode(userPublicId)}${query({
      client_id: clientId,
      include_global: includeGlobal ? "true" : null,
      name,
      key,
      page,
      page_size: pageSize,
      ...sortQuery(sort, direction),
    })}`
  );

  return {
    ...result,
    records: Array.isArray(result?.records) ? result.records : [],
    user: result?.user ?? null,
    pagination: normalizePagination(result, { page, pageSize }),
    sort: result?.sort ?? sort,
    direction: result?.direction ?? direction,
  };
};

export const saveUserAppData = async (userPublicId, payload, { clientId = null } = {}) =>
  adminRequest("POST", `${BASE}/users/${encode(userPublicId)}`, {
    mode: "upsert",
    ...payload,
    ...(clientId ? { client_id: clientId } : {}),
  });

/** Cross-user query (client-scoped; the endpoint excludes global rows). */
export const queryUserAppData = async ({
  clientId = null,
  q = null,
  name = null,
  key = null,
  email = null,
  userId = null,
  page = 1,
  pageSize = 50,
  sort = null,
  direction = null,
} = {}) => {
  const result = await adminRequest(
    "GET",
    `${BASE}/users${query({
      client_id: clientId,
      q,
      name,
      key,
      email,
      user_id: userId,
      page,
      page_size: pageSize,
      ...sortQuery(sort, direction),
    })}`
  );

  return {
    ...result,
    records: Array.isArray(result?.records) ? result.records : [],
    total: Number(result?.total ?? result?.count ?? 0),
    pagination: normalizePagination(result, { page, pageSize }),
    sort: result?.sort ?? sort,
    direction: result?.direction ?? direction,
  };
};

/* ------------------------------------------------------------ generic rows */

export const getAppDataRow = async (rowPublicId, { clientId = null } = {}) =>
  adminRequest("GET", `${BASE}/rows/${encode(rowPublicId)}${query({ client_id: clientId })}`);

export const updateAppDataRow = async (rowPublicId, payload, { clientId = null } = {}) =>
  adminRequest("PUT", `${BASE}/rows/${encode(rowPublicId)}${query({ client_id: clientId })}`, payload);

export const deleteAppDataRow = async (rowPublicId, { force = false, clientId = null } = {}) =>
  adminRequest(
    "DELETE",
    `${BASE}/rows/${encode(rowPublicId)}${query({ client_id: clientId, force: force ? "true" : null })}`
  );

export default getGlobalGroups;
